import is from '@sindresorhus/is';
import { useCallback, useEffect, useRef } from 'react';

import type {
    ErrorEvent as ChatErrorEvent,
    CompleteEvent,
    ContextRefreshEvent,
    MessageStartEvent,
    StructuredResponseEvent,
    UserMessage,
} from '../../data-contract/chat';
import { useSendChatMessage } from '../api';

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_BASE_DELAY_MS = 500;

export type ChatStreamListeners = {
    readonly onChunk: (text: string) => void;
    readonly onMessageStart: (event: MessageStartEvent) => void;
    readonly onStructuredResponse: (event: StructuredResponseEvent) => void;
    readonly onContextRefresh: (event: ContextRefreshEvent) => void;
    readonly onComplete: (event: CompleteEvent) => void;
    readonly onError: (event: ChatErrorEvent) => void;
    readonly onConnectionLost: () => void;
};

export type UseChatStreamReturn = {
    readonly connect: (threadId: string, body: UserMessage) => void;
    readonly disconnect: () => void;
};

export const useChatStream = (listeners: ChatStreamListeners): UseChatStreamReturn => {
    const chatStream = useSendChatMessage();

    const listenersRef = useRef(listeners);
    listenersRef.current = listeners;

    const lastEventIdRef = useRef<string | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activeThreadIdRef = useRef<string | null>(null);
    const isCompleteRef = useRef(false);
    const openRef = useRef<(threadId: string, body: UserMessage) => void>(() => undefined);

    const clearReconnectTimer = useCallback((): void => {
        if (!is.null(reconnectTimerRef.current)) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
    }, []);

    const scheduleReconnect = useCallback((): void => {
        const attempt = reconnectAttemptsRef.current + 1;
        if (attempt > MAX_RECONNECT_ATTEMPTS) {
            listenersRef.current.onConnectionLost();
            return;
        }
        reconnectAttemptsRef.current = attempt;
        clearReconnectTimer();
        reconnectTimerRef.current = setTimeout(
            () => {
                reconnectTimerRef.current = null;
                const threadId = activeThreadIdRef.current;
                if (is.null(threadId)) return;
                openRef.current(threadId, { resume: true, type: 'resume' });
            },
            RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1),
        );
    }, [clearReconnectTimer]);

    const open = useCallback(
        (threadId: string, body: UserMessage): void => {
            chatStream.send(
                {
                    body,
                    lastEventId: is.null(lastEventIdRef.current) ? undefined : lastEventIdRef.current,
                    threadId,
                },
                {
                    chunk: (text) => {
                        reconnectAttemptsRef.current = 0;
                        listenersRef.current.onChunk(text);
                    },
                    complete: (event) => {
                        lastEventIdRef.current = event.eventId;
                        reconnectAttemptsRef.current = 0;
                        isCompleteRef.current = true;
                        clearReconnectTimer();
                        listenersRef.current.onComplete(event);
                    },
                    error: (event) => {
                        if (event instanceof Error) {
                            if (isCompleteRef.current) return;
                            scheduleReconnect();
                            return;
                        }
                        lastEventIdRef.current = event.eventId;
                        clearReconnectTimer();
                        reconnectAttemptsRef.current = 0;
                        listenersRef.current.onError(event);
                    },
                    messageStart: (event) => {
                        lastEventIdRef.current = event.eventId;
                        reconnectAttemptsRef.current = 0;
                        listenersRef.current.onMessageStart(event);
                    },
                    structuredResponse: (event) => {
                        lastEventIdRef.current = event.eventId;
                        reconnectAttemptsRef.current = 0;
                        listenersRef.current.onStructuredResponse(event);
                    },
                    contextRefresh: (event) => {
                        lastEventIdRef.current = event.eventId;
                        reconnectAttemptsRef.current = 0;
                        listenersRef.current.onContextRefresh(event);
                    },
                },
            );
        },
        [chatStream, clearReconnectTimer, scheduleReconnect],
    );

    openRef.current = open;

    const connect = useCallback(
        (threadId: string, body: UserMessage): void => {
            clearReconnectTimer();
            reconnectAttemptsRef.current = 0;
            lastEventIdRef.current = null;
            isCompleteRef.current = false;
            activeThreadIdRef.current = threadId;
            open(threadId, body);
        },
        [clearReconnectTimer, open],
    );

    const disconnect = useCallback((): void => {
        clearReconnectTimer();
        reconnectAttemptsRef.current = 0;
        chatStream.stop();
    }, [chatStream, clearReconnectTimer]);

    useEffect(() => () => clearReconnectTimer(), [clearReconnectTimer]);

    return { connect, disconnect };
};
